export interface MembershipOverlayLike {
  state?: string | null;
  source?: string | null;
  canUndoManualNotAFit?: boolean;
}

export interface MembershipSnapshot {
  currentStatusCode: string | null;
  overlayState: string | null;
  overlaySource: string | null;
  weightedReliability: number | null;
  weightedFeedbackScore: number | null;
}

export type MembershipRecalcSignal =
  | "complete"
  | "queued"
  | "skipped"
  | "failed"
  | "unknown";

export interface MembershipRecalcClassification {
  signal: MembershipRecalcSignal;
  reason: string | null;
}

export type MembershipReconciliationStatus =
  | "complete"
  | "skipped"
  | "failed"
  | "timeout"
  | "cancelled";

export interface MembershipReconciliationOutcome {
  status: MembershipReconciliationStatus;
  reason: string | null;
  snapshot: MembershipSnapshot | null;
  attempts: number;
}

export interface ReconcileMembershipOptions {
  baseline: MembershipSnapshot | null;
  response: any;
  readSnapshot: () => Promise<MembershipSnapshot>;
  wait?: (milliseconds: number) => Promise<void>;
  retryDelaysMs?: number[];
  shouldContinue?: () => boolean;
}

export interface CorrectionRefreshOutcome {
  listRefreshed: boolean;
  detailsRefreshed: boolean;
  statsRefreshed: boolean;
  cancelled: boolean;
}

export type RatingCorrectionRefreshStatus =
  | "refreshed"
  | "source_failed"
  | "cancelled"
  | "failed";

export type ManualNotAFitAction = "Not a Fit" | "Undo Manual Not a Fit";

export type RatingCorrectionSource = "feedback" | "job";

export interface AdminSourceCorrectionEvidence {
  reason: string;
  evidenceReference: string;
}

export function canUndoManualNotAFit(
  overlay: MembershipOverlayLike | null | undefined
): boolean {
  return !!(
    overlay &&
    overlay.state === "not_a_fit" &&
    overlay.source === "support_manual" &&
    overlay.canUndoManualNotAFit === true
  );
}

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

export function classifyMembershipRecalc(
  response: any
): MembershipRecalcClassification {
  const recalc = response?.membershipRecalc || response?.recalculation || null;
  const skippedReason = normalizeText(recalc?.skippedReason);
  const status = normalizeText(
    recalc?.status ||
      response?.membershipRecalcStatus ||
      response?.recalculationStatus
  );
  const errorReason =
    recalc?.error ||
    response?.membershipRecalcError ||
    response?.recalculationError ||
    null;

  if (
    skippedReason === "error" ||
    ["failed", "failure", "error"].includes(status) ||
    errorReason
  ) {
    return {
      signal: "failed",
      reason: String(errorReason || recalc?.skippedReason || status || "error")
    };
  }

  if (skippedReason) {
    return {
      signal: "skipped",
      reason: String(recalc.skippedReason)
    };
  }

  if (["queued", "pending", "enqueued", "processing"].includes(status)) {
    return { signal: "queued", reason: null };
  }

  if (
    recalc &&
    (recalc.computedAt ||
      typeof recalc.didChange === "boolean" ||
      Object.prototype.hasOwnProperty.call(recalc, "newCurrentStatus"))
  ) {
    return { signal: "complete", reason: null };
  }

  if (["complete", "completed", "succeeded", "success"].includes(status)) {
    return { signal: "complete", reason: null };
  }

  return { signal: "unknown", reason: null };
}

function nullableNumber(value: unknown): number | null {
  if (value === null || typeof value === "undefined" || value === "") {
    return null;
  }
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

export function buildMembershipSnapshot(
  profile: any,
  stats: any
): MembershipSnapshot {
  const overlay = profile?.membership?.overlay || null;
  const fairFreshStart = stats?.fairFreshStart || null;

  return {
    currentStatusCode:
      profile?.membership?.currentStatusCode ||
      profile?.currentStatusCode ||
      fairFreshStart?.status?.currentStatusCode ||
      null,
    overlayState:
      overlay?.state || fairFreshStart?.recoveryState?.overlayState || null,
    overlaySource:
      overlay?.source || fairFreshStart?.recoveryState?.sourceSystem || null,
    weightedReliability: nullableNumber(
      fairFreshStart?.reliability?.weightedReliability
    ),
    weightedFeedbackScore: nullableNumber(
      fairFreshStart?.feedback?.weightedFeedbackScore
    )
  };
}

export function membershipSnapshotChanged(
  baseline: MembershipSnapshot,
  current: MembershipSnapshot
): boolean {
  const membershipFields: Array<keyof MembershipSnapshot> = [
    "currentStatusCode",
    "overlayState",
    "overlaySource"
  ];
  return membershipFields.some(key => baseline[key] !== current[key]);
}

const defaultWait = (milliseconds: number) =>
  new Promise<void>(resolve => setTimeout(resolve, milliseconds));

export async function reconcileMembershipAfterCorrection(
  options: ReconcileMembershipOptions
): Promise<MembershipReconciliationOutcome> {
  const classification = classifyMembershipRecalc(options.response);
  const shouldContinue = options.shouldContinue || (() => true);
  const cancelledOutcome = (): MembershipReconciliationOutcome => ({
    status: "cancelled",
    reason: "Talent selection or reconciliation request changed",
    snapshot: null,
    attempts: 0
  });

  if (!shouldContinue()) {
    return cancelledOutcome();
  }

  const readOnce = async (): Promise<MembershipSnapshot | null> => {
    try {
      return await options.readSnapshot();
    } catch {
      return null;
    }
  };

  if (
    classification.signal === "complete" ||
    classification.signal === "skipped" ||
    classification.signal === "failed"
  ) {
    const snapshot = await readOnce();
    if (!shouldContinue()) {
      return cancelledOutcome();
    }
    const refreshFailed = classification.signal === "complete" && !snapshot;
    return {
      status: refreshFailed ? "failed" : classification.signal,
      reason: refreshFailed
        ? "Unable to refresh membership state"
        : classification.reason,
      snapshot,
      attempts: 1
    };
  }

  const delays = options.retryDelaysMs || [400, 800, 1200, 1600, 2000];
  const wait = options.wait || defaultWait;
  let latestSnapshot: MembershipSnapshot | null = null;
  let successfulReads = 0;

  for (const delay of delays) {
    if (!shouldContinue()) {
      return cancelledOutcome();
    }
    await wait(delay);
    if (!shouldContinue()) {
      return cancelledOutcome();
    }
    latestSnapshot = await readOnce();
    if (!latestSnapshot) {
      continue;
    }
    successfulReads += 1;
    if (
      options.baseline &&
      membershipSnapshotChanged(options.baseline, latestSnapshot)
    ) {
      return {
        status: "complete",
        reason: null,
        snapshot: latestSnapshot,
        attempts: successfulReads
      };
    }
  }

  return {
    status: successfulReads === 0 ? "failed" : "timeout",
    reason:
      successfulReads === 0
        ? "Unable to refresh membership state"
        : "Membership recalculation is still pending",
    snapshot: latestSnapshot,
    attempts: successfulReads
  };
}

export function correctionResultMessage(
  correctionLabel: "Cancellations" | "Feedback rating",
  outcome: MembershipReconciliationOutcome
): { text: string; color: "success" | "warning" | "error" } {
  if (outcome.status === "complete") {
    return {
      text: `${correctionLabel} saved and membership state refreshed`,
      color: "success"
    };
  }

  if (outcome.status === "skipped") {
    return {
      text: `${correctionLabel} saved; membership recalculation was skipped${
        outcome.reason ? `: ${outcome.reason}` : ""
      }`,
      color: "warning"
    };
  }

  if (outcome.status === "timeout") {
    return {
      text: `${correctionLabel} saved, but membership recalculation is still pending. Refresh again shortly.`,
      color: "warning"
    };
  }

  if (outcome.status === "cancelled") {
    return {
      text: `${correctionLabel} saved; automatic refresh stopped because the selected Talent changed.`,
      color: "warning"
    };
  }

  return {
    text: `${correctionLabel} saved, but membership recalculation could not be confirmed${
      outcome.reason ? `: ${outcome.reason}` : ""
    }`,
    color: "warning"
  };
}

export async function refreshCorrectionViews(options: {
  refreshList: () => Promise<boolean | void>;
  shouldContinue: () => boolean;
  refreshDetails: () => Promise<boolean | void>;
  isStatsOpen: () => boolean;
  refreshStats: () => Promise<boolean | void>;
}): Promise<CorrectionRefreshOutcome> {
  const cancelled = (): CorrectionRefreshOutcome => ({
    listRefreshed: true,
    detailsRefreshed: false,
    statsRefreshed: false,
    cancelled: true
  });

  const listResult = await options.refreshList();
  if (listResult === false) {
    throw new Error("Unable to refresh the Talent list");
  }
  if (!options.shouldContinue()) {
    return cancelled();
  }

  const detailsResult = await options.refreshDetails();
  if (detailsResult === false) {
    if (!options.shouldContinue()) {
      return cancelled();
    }
    throw new Error("Unable to refresh Talent details");
  }
  if (!options.shouldContinue()) {
    return {
      listRefreshed: true,
      detailsRefreshed: true,
      statsRefreshed: false,
      cancelled: true
    };
  }

  if (!options.isStatsOpen()) {
    return {
      listRefreshed: true,
      detailsRefreshed: true,
      statsRefreshed: false,
      cancelled: false
    };
  }

  const statsResult = await options.refreshStats();
  if (statsResult === false) {
    if (!options.shouldContinue()) {
      return {
        listRefreshed: true,
        detailsRefreshed: true,
        statsRefreshed: false,
        cancelled: true
      };
    }
    throw new Error("Unable to refresh Talent statistics");
  }
  if (!options.shouldContinue()) {
    return {
      listRefreshed: true,
      detailsRefreshed: true,
      statsRefreshed: true,
      cancelled: true
    };
  }
  return {
    listRefreshed: true,
    detailsRefreshed: true,
    statsRefreshed: true,
    cancelled: false
  };
}

export async function refreshRatingCorrectionViews(options: {
  refreshSource: () => Promise<boolean>;
  refreshTalent: () => Promise<"refreshed" | "cancelled" | "failed">;
}): Promise<RatingCorrectionRefreshStatus> {
  const sourceRefreshed = await options.refreshSource();
  if (!sourceRefreshed) {
    return "source_failed";
  }
  return options.refreshTalent();
}

export async function runManualNotAFitAction(options: {
  action: ManualNotAFitAction;
  hygienistId: number;
  idempotencyKey: string;
  payload: { ticketReference: string; note: string };
  apply: (
    hygienistId: number,
    payload: { ticketReference: string; note: string },
    idempotencyKey: string
  ) => Promise<any>;
  undo: (
    hygienistId: number,
    payload: { ticketReference: string; note: string },
    idempotencyKey: string
  ) => Promise<any>;
}): Promise<string> {
  if (options.action === "Undo Manual Not a Fit") {
    await options.undo(
      options.hygienistId,
      options.payload,
      options.idempotencyKey
    );
    return "Manual Not a Fit removed";
  }

  await options.apply(
    options.hygienistId,
    options.payload,
    options.idempotencyKey
  );
  return "Not a Fit applied";
}

export async function runManualBreakAction(options: {
  hygienistId: number;
  durationMonths: number;
  note: string;
  evidenceReference: string;
  idempotencyKey: string;
  apply: (
    hygienistId: number,
    payload: {
      durationMonths: number;
      note: string;
      evidenceReference: string;
    },
    idempotencyKey: string
  ) => Promise<any>;
}): Promise<void> {
  await options.apply(
    options.hygienistId,
    {
      durationMonths: options.durationMonths,
      note: options.note.trim(),
      evidenceReference: options.evidenceReference.trim()
    },
    options.idempotencyKey
  );
}

export async function runMemberReviewNotAFitAction(options: {
  actionLabel: "Hold" | "Block";
  hygienistId: number;
  reason: string;
  ticketReference: string;
  reasonCategory: "credential_review" | "administrative_block";
  idempotencyKey: string;
  apply: (
    hygienistId: number,
    payload: {
      note: string;
      ticketReference: string;
      reasonCategory: "credential_review" | "administrative_block";
    },
    idempotencyKey: string
  ) => Promise<any>;
}): Promise<void> {
  await options.apply(
    options.hygienistId,
    {
      note: `${
        options.actionLabel
      } (new-member review): ${options.reason.trim()}`,
      ticketReference: options.ticketReference.trim(),
      reasonCategory: options.reasonCategory
    },
    options.idempotencyKey
  );
}

export async function runRatingSourceCorrectionAction(options: {
  sourceType: RatingCorrectionSource;
  sourceId: number;
  field: string;
  value: number;
  reason: string;
  evidenceReference: string;
  idempotencyKey: string;
  updateFeedback: (
    sourceId: number,
    field: string,
    value: number,
    correction: AdminSourceCorrectionEvidence,
    idempotencyKey: string
  ) => Promise<any>;
  updateJob: (
    sourceId: number,
    field: string,
    value: number,
    correction: AdminSourceCorrectionEvidence,
    idempotencyKey: string
  ) => Promise<any>;
}): Promise<any> {
  const update =
    options.sourceType === "feedback"
      ? options.updateFeedback
      : options.updateJob;
  return update(
    options.sourceId,
    options.field,
    options.value,
    {
      reason: options.reason.trim(),
      evidenceReference: options.evidenceReference.trim()
    },
    options.idempotencyKey
  );
}

export function actionableAdminActionError(
  error: unknown,
  fallback: string
): string {
  const message =
    error && typeof error === "object" && "message" in error
      ? (error as { message?: unknown }).message
      : null;
  return typeof message === "string" && message.trim() ? message : fallback;
}
